import { Link, useNavigate } from 'react-router-dom';
import { Upsell } from '../../components/plan/Upsell';
import { useBrandAllowance } from '../../components/plan/useBrandAllowance';
import { formatDate } from '../../lib/format';
import type { BusinessRead } from '../../types/api';
import { brandKindCopy } from '../onboarding/brandKind';
import { useAccount } from '../onboarding/useAccount';

/**
 * Every brand on the account, with the active one marked and the way to
 * switch or add. Mirrors the sidebar switcher so settings is a complete
 * picture of the account, not one brand at a time.
 *
 * The count is against the plan's brand allowance. A brand past it (after a
 * downgrade) is marked Locked: still switchable and readable, every write
 * refused until an upgrade or a delete brings the account under the cap.
 * A brand the user was invited to is tagged Member: it is someone else's, and
 * its delete and brief stay with that owner.
 */
export function BrandsCard() {
  const { business, businesses, setActiveBusinessId } = useAccount();
  const navigate = useNavigate();

  function activate(entry: BusinessRead) {
    if (entry.id === business?.id) return;
    setActiveBusinessId(entry.id);
    void navigate('/', { replace: true });
  }

  const allowance = useBrandAllowance();

  return (
    <section className="wizard settings-card">
      <div className="settings-card__head">
        <h2 className="wizard__title">
          Brands{' '}
          <span className="settings-card__count">
            {typeof allowance.limit === 'number'
              ? `${allowance.owned} of ${allowance.limit}`
              : businesses.length}
          </span>
        </h2>
        <p className="settings-card__lede">
          Each brand has its own brand profile, plans, news and history. The one marked active is
          what every screen shows.
        </p>
      </div>

      <ul className="brand-list">
        {businesses.map((entry) => {
          const isActive = entry.id === business?.id;
          const copy = brandKindCopy(entry.kind);
          return (
            <li className={isActive ? 'brand-row brand-row--active' : 'brand-row'} key={entry.id}>
              <div className="brand-row__main">
                <span className="brand-row__name">{entry.name}</span>
                <span className="brand-row__meta">
                  <span className="badge badge--neutral">{copy.tag}</span>
                  {entry.role === 'member' ? (
                    <span className="badge badge--member">Member</span>
                  ) : null}
                  {entry.locked ? <span className="badge badge--locked">Locked</span> : null}
                  <span className="brand-row__fact">
                    {entry.role === 'owner' ? 'Owner · since' : 'since'}{' '}
                    {formatDate(entry.created_at)}
                  </span>
                </span>
              </div>
              {isActive ? (
                <span className="brand-row__active">Active</span>
              ) : (
                <button className="button" type="button" onClick={() => activate(entry)}>
                  Switch
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <div className="settings-card__foot">
        {allowance.fields ? (
          <Upsell fields={allowance.fields} />
        ) : (
          <>
            <p className="field__hint">A second business, or your own personal brand beside it.</p>
            <Link className="button button--primary" to="/brands/new">
              Add a brand
            </Link>
          </>
        )}
      </div>
    </section>
  );
}
